import { authErrorMessage, t } from '@glory/i18n';
import { AuthLayout, Button, Callout, CheckboxField, EmailField, PasswordField, TextField } from '@glory/ui';
import { isEmail, normalizeEmail } from '@glory/ui/fields/parsers';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { type SubmitEvent, useEffect, useEffectEvent, useState } from 'react';

import { authClient, authErrorCode, conditionalPasskeyAvailable } from '../lib/auth-client.ts';
import { meQuery, resetSession, safeRedirect } from '../lib/session.ts';

interface LoginSearch {
  readonly redirect?: string | undefined;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    // Уже вошли — сразу туда, куда шли.
    const me = await context.queryClient.query(meQuery).catch(() => null);
    if (me) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

type Step = 'credentials' | 'totp' | 'backup';
type Pending = 'password' | 'passkey' | 'code' | null;

const TOTP_LENGTH = 6;

function LoginPage() {
  const search = Route.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  const finish = async () => {
    await resetSession(queryClient);
    await router.navigate({ href: safeRedirect(search.redirect), replace: true });
  };

  // Браузер предлагает passkey прямо в поле email (conditional UI), если он есть на устройстве.
  const onAutofillSignIn = useEffectEvent(async () => {
    await finish();
  });
  useEffect(() => {
    let mounted = true;
    // Функция, а не переменная: после await значение нужно перечитать.
    const stillMounted = () => mounted;
    void conditionalPasskeyAvailable().then(async (available) => {
      if (!available || !stillMounted()) return;
      const result = await authClient.signIn.passkey({ autoFill: true });
      if (stillMounted() && !result.error) await onAutofillSignIn();
    });
    return () => {
      mounted = false;
    };
  }, []);

  const signInWithPassword = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = normalizeEmail(email);
    const badEmail = isEmail(address) ? null : t('fields.emailInvalid');
    const noPassword = password === '' ? t('validation.required') : null;
    setEmailError(badEmail);
    setPasswordError(noPassword);
    if (badEmail || noPassword) return;

    setPending('password');
    setError(null);
    const { data, error: failure } = await authClient.signIn.email({ email: address, password });
    setPending(null);
    if (failure) {
      setError(authErrorMessage(failure));
      return;
    }
    if ('twoFactorRedirect' in data && data.twoFactorRedirect) {
      setStep('totp');
      return;
    }
    await finish();
  };

  const signInWithPasskey = async () => {
    setPending('passkey');
    setError(null);
    const { error: failure } = await authClient.signIn.passkey();
    setPending(null);
    if (failure) {
      if (authErrorCode(failure) !== 'AUTH_CANCELLED') setError(authErrorMessage(failure));
      return;
    }
    await finish();
  };

  const verify = async (value: string) => {
    if (value.trim() === '') {
      setError(t('validation.required'));
      return;
    }
    setPending('code');
    setError(null);
    const { error: failure } =
      step === 'totp'
        ? await authClient.twoFactor.verifyTotp({ code: value, trustDevice })
        : await authClient.twoFactor.verifyBackupCode({ code: value.trim(), trustDevice });
    setPending(null);
    if (failure) {
      setError(authErrorMessage(failure));
      if (authErrorCode(failure) === 'INVALID_TWO_FACTOR_COOKIE') setStep('credentials');
      return;
    }
    await finish();
  };

  if (step !== 'credentials') {
    const totp = step === 'totp';
    return (
      <AuthLayout
        title={t('auth.twoFactorTitle')}
        lead={totp ? t('auth.twoFactorLead') : t('auth.backupCodeLead')}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void verify(code);
          }}
        >
          <TextField
            key={step}
            label={totp ? t('auth.code') : t('auth.backupCode')}
            size="lg"
            autoFocus
            inputMode={totp ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            inputClassName="font-display tracking-[0.2em]"
            value={code}
            onValueChange={(next) => {
              const value = totp ? next.replace(/\D/g, '').slice(0, TOTP_LENGTH) : next;
              setCode(value);
              // Шесть цифр — отправляем сразу, как приложения банков.
              if (totp && value.length === TOTP_LENGTH && pending === null) void verify(value);
            }}
          />
          <CheckboxField
            label={t('auth.trustDevice')}
            checked={trustDevice}
            onCheckedChange={setTrustDevice}
          />
          {error ? <Callout>{error}</Callout> : null}
          <Button type="submit" variant="primary" size="lg" loading={pending === 'code'}>
            {t('auth.verify')}
          </Button>
        </form>
        <div className="flex flex-col items-start gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStep(totp ? 'backup' : 'totp');
              setCode('');
              setError(null);
            }}
          >
            {totp ? t('auth.useBackupCode') : t('auth.useTotp')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStep('credentials');
              setCode('');
              setError(null);
            }}
          >
            {t('auth.back')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.loginTitle')} lead={t('auth.loginLead')}>
      <form className="flex flex-col gap-4" noValidate onSubmit={(e) => void signInWithPassword(e)}>
        <EmailField
          label={t('auth.email')}
          size="lg"
          autoFocus
          autoComplete="username webauthn"
          value={email}
          onValueChange={(value) => {
            setEmail(value);
            setEmailError(null);
          }}
          error={emailError}
        />
        <PasswordField
          label={t('auth.password')}
          size="lg"
          autoComplete="current-password"
          value={password}
          onValueChange={(value) => {
            setPassword(value);
            setPasswordError(null);
          }}
          error={passwordError}
          aside={
            <Link
              to="/forgot-password"
              search={{ email: isEmail(normalizeEmail(email)) ? normalizeEmail(email) : undefined }}
              className="font-semibold text-text underline decoration-border underline-offset-2 hover:decoration-text"
            >
              {t('auth.forgotPassword')}
            </Link>
          }
        />
        {error ? <Callout>{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" loading={pending === 'password'}>
          {t('auth.signIn')}
        </Button>
      </form>
      <div className="flex items-center gap-3 text-small text-muted">
        <span className="h-px flex-1 bg-border" />
        {t('auth.or')}
        <span className="h-px flex-1 bg-border" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Button size="lg" loading={pending === 'passkey'} onClick={() => void signInWithPasskey()}>
          <KeyRound /> {t('auth.signInWithPasskey')}
        </Button>
        <p className="text-center text-caption text-muted">{t('auth.passkeyHint')}</p>
      </div>
    </AuthLayout>
  );
}
